import { Alert, Card, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useApprovalRequest } from "../../../features/approvals/queries.js";
import { ApprovalRequestPanel } from "../../../features/approvals/request-panel.js";
import {
  ApprovalDocLink,
  ApprovalsPage,
  ApprovalStatusBadge,
  docTypeLabel,
  useApprovalsCrumb,
} from "../../../features/approvals/shared.js";
import { Amount } from "../../../features/accounting/shared.js";
import { errorMessage } from "../../../lib/errors.js";

export const Route = createFileRoute("/_app/onaylar/$id")({ component: ApprovalDetailPage });

function ApprovalDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useApprovalsCrumb();
  const request = useApprovalRequest(id);
  const title = request.data ? request.data.title : t("approvals.detail.title");

  return (
    <ApprovalsPage title={title} breadcrumb={[crumb, { label: title }]}>
      {request.error ? (
        <Alert tone="danger">{errorMessage(request.error)}</Alert>
      ) : !request.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <div className="grid gap-4">
          <Card className="flex flex-wrap items-center gap-3 p-4">
            <StatusBadge tone="neutral">{docTypeLabel(request.data.docType)}</StatusBadge>
            <ApprovalStatusBadge status={request.data.status} />
            {request.data.amount ? (
              <span className="text-lg font-semibold">
                <Amount value={request.data.amount} />
              </span>
            ) : null}
            <span className="ms-auto text-sm">
              <ApprovalDocLink
                docType={request.data.docType}
                docId={request.data.docId}
                label={t("approvals.detail.openDocument")}
                summary={request.data.summary as Record<string, unknown>}
              />
            </span>
          </Card>
          <ApprovalRequestPanel requestId={id} />
        </div>
      )}
    </ApprovalsPage>
  );
}
